import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { HomeClubSectionFlow, Nullable } from 'corgi-types';
import type { DisplayProps, HeightProps } from 'styled-system';
import { display, height, space } from 'styled-system';

import CardGrid from '../../../card-grid/components/CardGrid';
import CommonUspTeaser from '../../../common-usp-teaser/components/CommonUspTeaser';
import HomeClubSection from '../../../home-club-section/components/HomeClubSection';
import OfferTeaser from '../../../offer-teaser/components/OfferTeaser';
import PictureText from '../../../picture-text/components/PictureText';
import SeoSection from '../../../seo-section/components/SeoSection';
import TopBanner from '../../../top-banner/components/TopBanner';
import { GridItem } from '../../components/Grid';
import type { SpaceProps } from '../../theme/system-types';
import { LayoutType, type Slices } from './types';

interface DrawerContentProps {
  flow: HomeClubSectionFlow;
  slices: Slices;
  trackingProps?: {
    eventName?: string;
    [key: string]: string;
  };
}

export const sliceMap = {
  ['seo-section']: SeoSection,
  ['top-banner']: TopBanner,
  ['card-grid']: CardGrid,
  ['common-usp-teaser']: CommonUspTeaser,
  ['offer-teaser']: OfferTeaser,
  ['picture-text']: PictureText,
  ['home-club-section']: HomeClubSection,
};

const gridStyles = {
  maxWidth: 1600,
  mx: 'auto',
  width: 1,
  px: 'sp_0',
};

const VerticalSpacing = styled.span<HeightProps & SpaceProps & DisplayProps>`
  ${display};
  ${height};
  ${space};
`;

const getSliceStyles = (type: string): Nullable<object> => {
  switch (type) {
    case 'top-banner':
      return {
        ...gridStyles,
        flexDirection: 'column',
        display: 'flex',
      };
    case 'seo-section':
      return { mb: 'sp_0', width: 1 };

    case 'offer-teaser':
      return {
        ...gridStyles,
        px: ['sp_0', 'sp_24', 'sp_48', 'sp_64'],
      };

    case 'common-usp-teaser':
      return {
        ...gridStyles,
        px: 'sp_0',
      };
    default:
      return {
        mx: 'auto',
        width: 1,
      };
  }
};

const getSliceProps = ({
  type,
  flow,
  trackingProps,
}: Omit<DrawerContentProps, 'slices'> & {
  type: string;
}): Partial<ComponentProps<typeof HomeClubSection>> => {
  switch (type) {
    case 'home-club-section':
      return {
        flow,
        ...(trackingProps && { trackingProps }),
      };

    default:
      return {};
  }
};

const DrawerContent = ({ flow, slices, trackingProps }: DrawerContentProps) => (
  <>
    {slices.map((sliceConfig, index) => {
      if ('corgiType' in sliceConfig) {
        const { corgiType, props } = sliceConfig;
        const Component = sliceMap[corgiType];

        return (
          <GridItem
            key={corgiType + index.toString()}
            mb={['sp_16', 'sp_20', 'sp_24', 'sp_32']}
            {...getSliceStyles(corgiType)}
          >
            <Component {...props} {...getSliceProps({ type: corgiType, flow, trackingProps })} />
          </GridItem>
        );
      }

      if (sliceConfig.layoutType === LayoutType.VerticalSpacing)
        return (
          <VerticalSpacing
            key={index}
            display="inline-block"
            mt={['sp_16', 'sp_20', 'sp_24', 'sp_32']}
          />
        );

      return null;
    })}
  </>
);

export default DrawerContent;
